import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { ChatManager, FunctionCall } from '@iai-lib/sdk';

export interface ChatMessage {
  role: 'user' | 'assistant' | 'error';
  content: string;
  sources?: { title: string }[];
  timestamp: Date;
}

@Injectable({
  providedIn: 'root'
})
export class SopAssistantService {
  isOpen = false;
  isLoading = false;
  messages: ChatMessage[] = [];
  screeningId: number = null;
  private chat: ChatManager = null;

  constructor(private http: HttpClient, private zone: NgZone) {}

  toggle() {
    this.isOpen = !this.isOpen;
  }

  open(screeningId?: number) {
    if (screeningId && screeningId !== this.screeningId) {
      this.reset();
      this.screeningId = screeningId;
    }
    this.isOpen = true;
  }

  close() {
    this.isOpen = false;
    this.reset();
  }

  reset() {
    if (this.chat) {
      this.chat.reset();
      this.chat.destroy();
      this.chat = null;
    }
    this.messages = [];
    this.screeningId = null;
    this.isLoading = false;
  }

  async send(message: string) {
    if (!message?.trim() || this.isLoading) return;

    this.messages.push({ role: 'user', content: message.trim(), timestamp: new Date() });
    this.isLoading = true;

    try {
      if (!this.chat) {
        await this.initChat();
      }
      await this.chat.sendUserMessage(message.trim());
    } catch (err) {
      this.zone.run(() => {
        this.messages.push({ role: 'error', content: 'Something went wrong. Please try again.', timestamp: new Date() });
        this.isLoading = false;
      });
    }
  }

  private async initChat() {
    const params: any = {};
    if (this.screeningId) params.screeningId = this.screeningId;

    this.chat = new ChatManager('a3a610d9-73fc-4524-9ca3-4e06fdb326a5', {
      params,
      historyKey: this.screeningId ? `screening-${this.screeningId}` : 'general',
      createThreadHandler: async ({ assistantId, params: threadParams }) => {
        const res: any = await this.http.post(`${environment.baseURl}/api/sop/thread`, {
          params: threadParams
        }).toPromise();
        return { id: res.id };
      }
    });

    this.chat.on('messages', (sdkMessages) => {
      this.zone.run(() => {
        this.messages = [];
        for (const msg of sdkMessages) {
          const textParts = (msg.parts || []).filter((p: any) => p.type === 'text');
          const content = textParts.map((p: any) => p.text).join('\n');
          if (content) {
            this.messages.push({
              role: msg.role === 'user' ? 'user' : 'assistant',
              content,
              timestamp: new Date()
            });
          }
        }
      });
    });

    this.chat.on('status', (status) => {
      this.zone.run(() => {
        this.isLoading = status === 'streaming' || status === 'submitted';
      });
    });

    this.chat.on('error', (error) => {
      this.zone.run(() => {
        this.messages.push({ role: 'error', content: error.message || 'An error occurred.', timestamp: new Date() });
        this.isLoading = false;
      });
    });

    this.chat.on('functionCall', (call: FunctionCall) => {
      console.log('GOT A FUNCTION CALL',call);
      this.zone.run(() => this.handleFunctionCall(call));
    });

    await this.chat.startChat();
  }

  private async handleFunctionCall(call: FunctionCall) {
    switch (call.name) {
      case 'highlightSopFields': {
        const result = this.highlightSopFields(call.params.fields, call.params.checkNames);
        await this.chat.respondToFunctionCall(call.id, result);
        break;
      }
      default:
        await this.chat.respondToFunctionCall(call.id, { error: `Unknown function: ${call.name}` });
    }
  }

  private highlightSopFields(fields?: string[], checkNames?: string[]): Record<string, any> {
    document.querySelectorAll('.sop-highlight').forEach(el => el.classList.remove('sop-highlight'));

    const highlighted: string[] = [];

    if (checkNames?.length) {
      for (const name of checkNames) {
        const el = document.querySelector(`[data-sop-check="${name}"]`);
        if (el) {
          el.classList.add('sop-highlight');
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          highlighted.push(`check:${name}`);
        }
      }
    }

    if (fields?.length) {
      for (const field of fields) {
        const el = document.querySelector(`[data-sop-field="${field}"]`);
        if (el) {
          el.classList.add('sop-highlight');
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          highlighted.push(`field:${field}`);
        }
      }
    }

    this.clearHighlightsAfterDelay();
    return { highlighted, found: highlighted.length };
  }

  private clearHighlightsAfterDelay(ms = 5000) {
    setTimeout(() => {
      document.querySelectorAll('.sop-highlight').forEach(el => {
        el.classList.remove('sop-highlight');
        el.classList.add('sop-highlight-out');
        el.addEventListener('animationend', () => el.classList.remove('sop-highlight-out'), { once: true });
      });
    }, ms);
  }
}
